import { useState } from 'react';
import type { Translate } from '@elec/i18n';
import type { Platform } from '@elec/platform';
import { legalUrl } from './cloud.ts';
import { Dialog } from './ui.tsx';

export const APP_VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';

/** Version, free-beta notice, data handling, and (desktop) update check. */
export function AboutDialog(props: {
  t: Translate;
  platform: Platform;
  aiMode: 'demo' | 'remote';
  locale: string;
  onClose(): void;
}) {
  const { t, platform } = props;
  const [update, setUpdate] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const check = async (install = false) => {
    setBusy(true);
    const r = await platform.checkUpdates!(install);
    setBusy(false);
    setUpdate(
      r.status === 'available'
        ? t('about.updateAvailable', { v: r.version ?? '' })
        : t(`about.update.${r.status}`),
    );
  };
  return (
    <Dialog
      title={t('about.title')}
      onClose={props.onClose}
      actions={<button onClick={props.onClose}>{t('dialog.close')}</button>}
    >
      <p>
        <strong>{t('app.name')}</strong> {APP_VERSION} · {t(`status.platform.${platform.kind}`)}
      </p>
      <p className="notice" data-testid="beta-notice">
        {t('about.beta')}
      </p>
      <p className="small">{t('about.data')}</p>
      <p className="small">{t(props.aiMode === 'demo' ? 'about.aiDemo' : 'about.aiRemote')}</p>
      <p className="small">{t('about.rules')}</p>
      <p className="small">
        <button
          className="link"
          onClick={() => void platform.openUrl(legalUrl('privacy', props.locale))}
          data-testid="about-privacy"
        >
          {t('legal.privacy')}
        </button>
        {' · '}
        <button className="link" onClick={() => void platform.openUrl(legalUrl('support', props.locale))}>
          {t('legal.support')}
        </button>
      </p>
      {platform.checkUpdates && (
        <p>
          <button onClick={() => void check(false)} disabled={busy}>
            {t('about.checkUpdates')}
          </button>{' '}
          {update && <span className="small">{update}</span>}
          {update?.includes(t('about.updateAvailableWord')) && (
            <button className="primary" onClick={() => void check(true)} disabled={busy}>
              {t('about.install')}
            </button>
          )}
        </p>
      )}
    </Dialog>
  );
}
